import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button, Icon, Input, Skeleton } from "@nexus/ui";
import { getLocalUserFacingApiMessage, passThroughMessage } from "@/lib/api-client";
import {
  llmModelPricesQueryOptions,
  useDeleteLlmModelPrice,
  useSaveLlmModelPrice,
} from "@/lib/queries";
import type {
  LlmModelPrice,
  LlmModelPriceInput,
} from "@/features/llm-usage/api/llm-model-prices.api";
import type { LlmProvider } from "@/features/llm-usage/api/llm-usage.api";
import { brandForProvider, humanizeModelId } from "@/features/llm-usage/lib/model-display";
import { formatRateUsd, microsToUsd, usdToMicros } from "@/features/llm-usage/lib/money";
import { ProviderIcon } from "./ProviderIcon";

// ─── Model price editor ─────────────────────────────────────────────
// Rates per million tokens, which is the unit every provider publishes and the
// only one an admin can check against a pricing page without arithmetic.
//
// Deliberately not seeded with defaults. Shipping guessed rates would render
// as authoritative dollar figures on the tab, and a confidently wrong bill is
// worse than an obviously absent one — so an unpriced model is shown as a gap
// to fill rather than quietly costed at zero.

/** The five rates a model carries, in the order the bill is usually read. */
const RATE_FIELDS = [
  { key: "inputPerMillion", label: "Input" },
  { key: "outputPerMillion", label: "Output" },
  { key: "thinkingPerMillion", label: "Thinking" },
  { key: "cacheWritePerMillion", label: "Cache write" },
  { key: "cacheReadPerMillion", label: "Cache read" },
] as const;

type RateKey = (typeof RATE_FIELDS)[number]["key"];

/** A row being edited, holding dollars as typed rather than as stored. */
type RateDraft = Record<RateKey, string>;

const EMPTY_DRAFT: RateDraft = {
  inputPerMillion: "",
  outputPerMillion: "",
  thinkingPerMillion: "",
  cacheWritePerMillion: "",
  cacheReadPerMillion: "",
};

function toDraft(price: LlmModelPrice): RateDraft {
  return {
    inputPerMillion: String(microsToUsd(price.inputPerMillion)),
    outputPerMillion: String(microsToUsd(price.outputPerMillion)),
    thinkingPerMillion: String(microsToUsd(price.thinkingPerMillion)),
    cacheWritePerMillion: String(microsToUsd(price.cacheWritePerMillion)),
    cacheReadPerMillion: String(microsToUsd(price.cacheReadPerMillion)),
  };
}

/** A blank or unparseable box means "this class is free", not "reject the save". */
function toMicros(value: string): number {
  const parsed = Number(value);
  if (value.trim() === "" || Number.isNaN(parsed) || parsed < 0) return 0;
  return usdToMicros(parsed);
}

export function ModelPriceEditor({ isReadOnly }: { isReadOnly: boolean }) {
  const { t } = useTranslation();
  const { data, isPending, isError, refetch } = useQuery(llmModelPricesQueryOptions());

  const save = useSaveLlmModelPrice();
  const remove = useDeleteLlmModelPrice();

  /** Which model is open for editing. Only one at a time, so the table stays a table. */
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<RateDraft>(EMPTY_DRAFT);

  if (isError) {
    return (
      <div className="flex flex-col items-start gap-3 px-5 py-4">
        <p className="m-0 text-xs text-muted-foreground">
          {t("admin.opsSettings.prices.loadError", "Could not load model prices.")}
        </p>
        <Button variant="outline" size="sm" onClick={() => void refetch()}>
          {t("admin.opsSettings.retry", "Try again")}
        </Button>
      </div>
    );
  }

  if (isPending) {
    return (
      <div className="px-5 py-4">
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  const key = (provider: LlmProvider, model: string) => `${provider}:${model}`;

  function startEditing(provider: LlmProvider, model: string, price?: LlmModelPrice) {
    setEditing(key(provider, model));
    setDraft(price ? toDraft(price) : EMPTY_DRAFT);
  }

  function handleSave(provider: LlmProvider, model: string) {
    const input: LlmModelPriceInput = {
      provider,
      model,
      inputPerMillion: toMicros(draft.inputPerMillion),
      outputPerMillion: toMicros(draft.outputPerMillion),
      thinkingPerMillion: toMicros(draft.thinkingPerMillion),
      cacheWritePerMillion: toMicros(draft.cacheWritePerMillion),
      cacheReadPerMillion: toMicros(draft.cacheReadPerMillion),
    };

    save.mutate(input, {
      onSuccess: () => {
        setEditing(null);
        toast.success(t("admin.opsSettings.prices.saved", "Price saved for {{model}}", { model }));
      },
      onError: (error) => {
        const message = getLocalUserFacingApiMessage(error, passThroughMessage);
        if (message) toast.error(message);
      },
    });
  }

  function handleRemove(provider: LlmProvider, model: string) {
    remove.mutate(
      { provider, model },
      {
        onSuccess: () =>
          toast.success(
            t("admin.opsSettings.prices.removed", "Price removed for {{model}}", { model }),
          ),
        onError: (error) => {
          const message = getLocalUserFacingApiMessage(error, passThroughMessage);
          if (message) toast.error(message);
        },
      },
    );
  }

  const hasNothing = data.prices.length === 0 && data.unpriced.length === 0;

  return (
    <div className="space-y-3 px-5 py-4">
      {hasNothing ? (
        <p className="m-0 text-xs text-muted-foreground">
          {t(
            "admin.opsSettings.prices.empty",
            "No models have been called recently, so there is nothing to price yet.",
          )}
        </p>
      ) : null}

      {data.unpriced.length > 0 ? (
        <p className="mt-0 mb-4 rounded-md border border-warning-border bg-warning-subtle px-3 py-2 text-xs text-warning-subtle-foreground">
          {t("admin.opsSettings.prices.unpricedWarning", {
            defaultValue_one:
              "{{count}} model has been called recently with no price set. Its tokens are counted, but its cost is not.",
            defaultValue_other:
              "{{count}} models have been called recently with no price set. Their tokens are counted, but their cost is not.",
            count: data.unpriced.length,
          })}
        </p>
      ) : null}

      <ul className="m-0 list-none space-y-2 p-0">
        {data.unpriced.map((model) => (
          <PriceRow
            key={key(model.provider, model.model)}
            provider={model.provider}
            model={model.model}
            price={undefined}
            isEditing={editing === key(model.provider, model.model)}
            isReadOnly={isReadOnly}
            isSaving={save.isPending}
            draft={draft}
            onDraftChange={setDraft}
            onStartEditing={() => startEditing(model.provider, model.model)}
            onCancel={() => setEditing(null)}
            onSave={() => handleSave(model.provider, model.model)}
            onRemove={() => handleRemove(model.provider, model.model)}
          />
        ))}

        {data.prices.map((price) => (
          <PriceRow
            key={key(price.provider, price.model)}
            provider={price.provider}
            model={price.model}
            price={price}
            isEditing={editing === key(price.provider, price.model)}
            isReadOnly={isReadOnly}
            isSaving={save.isPending}
            draft={draft}
            onDraftChange={setDraft}
            onStartEditing={() => startEditing(price.provider, price.model, price)}
            onCancel={() => setEditing(null)}
            onSave={() => handleSave(price.provider, price.model)}
            onRemove={() => handleRemove(price.provider, price.model)}
          />
        ))}
      </ul>
    </div>
  );
}

interface PriceRowProps {
  provider: LlmProvider;
  model: string;
  /** Undefined for a model that has been called but never priced. */
  price: LlmModelPrice | undefined;
  isEditing: boolean;
  isReadOnly: boolean;
  isSaving: boolean;
  draft: RateDraft;
  onDraftChange: (draft: RateDraft) => void;
  onStartEditing: () => void;
  onCancel: () => void;
  onSave: () => void;
  onRemove: () => void;
}

function PriceRow({
  provider,
  model,
  price,
  isEditing,
  isReadOnly,
  isSaving,
  draft,
  onDraftChange,
  onStartEditing,
  onCancel,
  onSave,
  onRemove,
}: PriceRowProps) {
  const { t } = useTranslation();

  return (
    <li className="rounded-md border border-border bg-background px-3 py-2.5">
      <div className="flex items-center gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-2">
          <ProviderIcon brand={brandForProvider(provider)} className="mt-0.5" />
          <div className="min-w-0">
            <p className="m-0 truncate text-xs font-medium text-foreground">
              {humanizeModelId(model)}
            </p>
            {/* The id stays on the line here, unlike the tables: this is the
                row where a rate is bound to it, and the binding is exact. */}
            <p className="m-0 mt-0.5 truncate text-[11px] text-muted-foreground">
              <span className="font-mono">{model}</span>
              {price ? ` · ${summary(price)}` : null}
            </p>
          </div>
        </div>

        {price ? null : (
          <span className="shrink-0 rounded-sm bg-warning-subtle px-1.5 py-0.5 text-[11px] text-warning-subtle-foreground">
            {t("admin.opsSettings.prices.noPrice", "No price")}
          </span>
        )}

        {isReadOnly ? null : (
          <div className="flex shrink-0 items-center gap-1">
            {isEditing ? null : (
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={onStartEditing}>
                {price
                  ? t("admin.opsSettings.prices.edit", "Edit")
                  : t("admin.opsSettings.prices.setPrice", "Set price")}
              </Button>
            )}
            {price && !isEditing ? (
              <Button
                variant="ghost"
                size="sm"
                className="size-7 p-0"
                aria-label={t("admin.opsSettings.prices.remove", "Remove price")}
                onClick={onRemove}
              >
                <Icon name="x" size="sm" color="muted" aria-hidden />
              </Button>
            ) : null}
          </div>
        )}
      </div>

      {isEditing ? (
        <div className="mt-3 space-y-2 border-t border-border/60 pt-3">
          <p className="m-0 text-[11px] text-muted-foreground">
            {t(
              "admin.opsSettings.prices.unitHelp",
              "US dollars per million tokens, as published on the provider's pricing page. Leave a box empty for a class this model does not bill.",
            )}
          </p>
          <div className="flex flex-wrap gap-3">
            {RATE_FIELDS.map((rate) => (
              <label key={rate.key} className="flex flex-col gap-1">
                <span className="text-[11px] text-muted-foreground">
                  {t(`admin.opsSettings.prices.${rate.key}`, rate.label)}
                </span>
                <Input
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="0.01"
                  value={draft[rate.key]}
                  disabled={isSaving}
                  onChange={(event) =>
                    onDraftChange({ ...draft, [rate.key]: event.target.value })
                  }
                  className="h-8 w-24 text-xs tabular-nums"
                />
              </label>
            ))}
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Button size="sm" className="h-7 text-xs" disabled={isSaving} onClick={onSave}>
              {isSaving
                ? t("admin.opsSettings.saving", "Saving…")
                : t("admin.opsSettings.prices.savePrice", "Save price")}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              disabled={isSaving}
              onClick={onCancel}
            >
              {t("admin.opsSettings.prices.cancel", "Cancel")}
            </Button>
          </div>
        </div>
      ) : null}
    </li>
  );
}

/** Input and output only: the two rates that dominate every bill. */
function summary(price: LlmModelPrice): string {
  return `in ${formatRateUsd(price.inputPerMillion)} · out ${formatRateUsd(price.outputPerMillion)} /1M`;
}
